'use client';

import React, { useCallback, useState } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { MotionLink } from '@/components/ui/Motion';
import { URLS } from '@/lib/consts';
import { cn } from '@/lib/utils';
import SectionBase from '../section-base';
import SectionHeader from '../section-header';
import {
  cardDescriptionMotionProps as cardDescriptionMotionProperties,
  cardSubTitleMotionProps as cardSubTitleMotionProperties,
} from './animations';
import {
  STARTING_LOCATION,
  SERVICE_AREAS_PRIORITY,
  SERVICE_AREAS_OTHER,
  type GoogleMapsLatLng,
} from './consts';

const TITLE = 'Do we service your area?';
const SECTION_ID = `service-areas`;

const MAP_OPTIONS = {
  clickableIcons: true,
  disableDefaultUI: true,
  zoomControl: true,
  scollwheel: true,
};

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? '';

function sortMapByKey<T>(map: Map<string, T>): [string, T][] {
  return [...map].sort(([aKey], [bKey]) => aKey.localeCompare(bKey));
}

interface ServiceAreasProperties {
  showCTA?: boolean;
  showHeader?: boolean;
  showMaps?: boolean;
  serviceAreas?: Map<string, GoogleMapsLatLng>;
  groupAreas?: boolean;
}

function TownListItem({
  name,
  isSelected,
  onClick,
}: {
  name: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 py-2 px-3 rounded-md text-sm transition-colors text-left cursor-pointer',
        isSelected
          ? 'bg-primary/10 text-primary border-l-2 border-primary font-medium'
          : 'hover:bg-muted text-muted-foreground',
      )}
    >
      <MapPin className="h-4 w-4 shrink-0" />
      <span>{name}</span>
    </button>
  );
}

function TownListGroup({
  label,
  areas,
  selectedArea,
  onSelect,
}: {
  label: string;
  areas: [string, GoogleMapsLatLng][];
  selectedArea: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-3">
        {label}
      </p>
      <div className="flex flex-col gap-0.5">
        {areas.map(([key]) => (
          <TownListItem
            key={key}
            name={key}
            isSelected={selectedArea === key}
            onClick={() => onSelect(key)}
          />
        ))}
      </div>
    </div>
  );
}

function ServiceAreasSection({
  serviceAreas = SERVICE_AREAS_PRIORITY,
  showCTA = true,
  showHeader = true,
  showMaps = true,
  groupAreas = false,
}: ServiceAreasProperties) {
  const [selectedArea, setSelectedArea] = useState(STARTING_LOCATION);
  const [zoom, setZoom] = useState(9);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: 'CORNERSTONE_HOMES_GOOGLE_MAPS',
  });

  const handleOnClick = useCallback((key: string) => {
    setZoom(12);
    setSelectedArea(key);
  }, []);

  const priorityAreas = sortMapByKey(SERVICE_AREAS_PRIORITY).filter(([key]) =>
    serviceAreas.has(key),
  );
  const otherAreas = sortMapByKey(SERVICE_AREAS_OTHER).filter(([key]) =>
    serviceAreas.has(key),
  );
  const allAreasSorted = sortMapByKey(serviceAreas);

  return (
    <SectionBase
      id={SECTION_ID}
      containerProps={{ id: SECTION_ID }}
      background="bg-background"
    >
      <div className="flex flex-col gap-6">
        {showHeader && (
          <motion.div
            className="flex flex-col items-center text-center"
            animate="visible"
            initial="hidden"
          >
            <SectionHeader
              subtitle="SERVICE AREAS"
              title={TITLE}
              description="We provide design, build, and renovate services throughout the Claremore, Oklahoma and the surrounding areas, including..."
              subtitleMotionProps={cardSubTitleMotionProperties}
              descriptionMotionProps={cardDescriptionMotionProperties}
            />
          </motion.div>
        )}

        <div className="flex flex-col lg:flex-row gap-4 w-full">
          {/* Town List Panel */}
          <div
            className={cn(
              'lg:w-[35%] shrink-0',
              groupAreas
                ? 'grid grid-cols-3 gap-1 lg:flex lg:flex-col lg:gap-4 lg:max-h-[500px] lg:overflow-y-auto lg:pr-2'
                : 'grid grid-cols-3 gap-1 lg:flex lg:flex-col lg:gap-0.5 lg:max-h-[500px] lg:overflow-y-auto lg:pr-2',
            )}
          >
            {groupAreas ? (
              <>
                {/* Priority group - desktop */}
                <div className="hidden lg:block">
                  <TownListGroup
                    label="Priority Areas"
                    areas={priorityAreas}
                    selectedArea={selectedArea}
                    onSelect={handleOnClick}
                  />
                </div>
                {/* Other group - desktop */}
                {otherAreas.length > 0 && (
                  <div className="hidden lg:block">
                    <TownListGroup
                      label="Additional Areas"
                      areas={otherAreas}
                      selectedArea={selectedArea}
                      onSelect={handleOnClick}
                    />
                  </div>
                )}
                {/* Mobile: flat compact grid */}
                {allAreasSorted.map(([key]) => (
                  <button
                    key={`mobile-${key}`}
                    type="button"
                    onClick={() => handleOnClick(key)}
                    className={cn(
                      'lg:hidden flex items-center gap-1.5 py-1.5 px-2 rounded text-xs transition-colors cursor-pointer',
                      selectedArea === key
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted text-muted-foreground',
                    )}
                  >
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span>{key}</span>
                  </button>
                ))}
              </>
            ) : (
              <>
                {/* Ungrouped desktop list */}
                {allAreasSorted.map(([key]) => (
                  <div key={key} className="hidden lg:block">
                    <TownListItem
                      name={key}
                      isSelected={selectedArea === key}
                      onClick={() => handleOnClick(key)}
                    />
                  </div>
                ))}
                {/* Mobile: compact grid */}
                {allAreasSorted.map(([key]) => (
                  <button
                    key={`mobile-${key}`}
                    type="button"
                    onClick={() => handleOnClick(key)}
                    className={cn(
                      'lg:hidden flex items-center gap-1.5 py-1.5 px-2 rounded text-xs transition-colors cursor-pointer',
                      selectedArea === key
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted text-muted-foreground',
                    )}
                  >
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span>{key}</span>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Map Panel */}
          {showMaps && isLoaded && (
            <div className="flex-1 rounded-lg border border-border overflow-hidden h-[400px] lg:h-[500px]">
              <GoogleMap
                mapContainerClassName="w-full h-full"
                center={serviceAreas.get(selectedArea)}
                zoom={zoom}
                options={MAP_OPTIONS}
              >
                {[...serviceAreas].map(([key, value]) => (
                  <MarkerF
                    key={key}
                    position={value}
                    onClick={() => {
                      handleOnClick(key);
                    }}
                  />
                ))}
              </GoogleMap>
            </div>
          )}
        </div>

        {showCTA && (
          <div className="flex justify-center">
            <Button asChild>
              <MotionLink href={URLS.ABOUT.OUR_SERVICE_AREAS}>
                View All Service Areas
              </MotionLink>
            </Button>
          </div>
        )}
      </div>
    </SectionBase>
  );
}

export default ServiceAreasSection;
